/*
 * Copyright (c) 2009 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.axis

import com.macrofocus.common.filter.MutableFilter
import com.macrofocus.common.interval.MutableBoundedInterval
import com.macrofocus.common.interval.SimpleBoundedInterval
import com.macrofocus.common.interval.TransformBoundedInterval
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.PropertyEvent
import com.macrofocus.common.properties.PropertyListener
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.common.scale.NumericTransform
import com.macrofocus.common.scale.Transform
import com.macrofocus.common.selection.Selection
import com.macrofocus.common.selection.SingleSelection
import com.macrofocus.high_d.axis.AxisModel.Companion.DATA
import org.molap.dataframe.*
import org.molap.series.Series
import org.molap.subset.OrdinalDimension
import org.molap.subset.SubsetDataFrame
import kotlin.math.sqrt
import kotlin.reflect.KClass

open class DefaultAxisModel<Row, C>(
    dataFrame: DataFrame<Row, C, *>,
    annotationDataFrame: DataFrame<*, C, *>?,
    subsetDataFrame: SubsetDataFrame<Row, C, *>?,
    column: C,
    probing: SingleSelection<Row>,
    selection: Selection<Row>,
    filter: MutableFilter<Row>
) : AbstractAxisModel<Row, C>() {
    protected val dataFrame: DataFrame<Row, C, *>
    override val column: C = column
    protected val series: Array<Series<Row,out Any?>?>
    override val dimension: OrdinalDimension<Row, C, out Any?>? = subsetDataFrame?.getOrdinalDimension(column)
    override val probing: SingleSelection<Row> = probing
    override val selection: Selection<Row> = selection
    override val filter: MutableFilter<Row> = filter
    override val interval: MutableBoundedInterval by lazy {
        val field = createBoundedInterval()
        addIntervalListener(field)
        field
    }
    override val scaledInterval: MutableBoundedInterval by lazy {
        val field = createScaledBoundedInterval()
        addIntervalListener(field)
        field
    }
    override val buttonModel: MutableProperty<Boolean> = SimpleProperty<Boolean>(false)
    protected var min = Double.MAX_VALUE
    protected var max = -Double.MAX_VALUE
    override final val mean: Double
    override final val stdDev: Double
    override val binCount: MutableProperty<Int> = SimpleProperty<Int>(0)
    override val binType: MutableProperty<BinType> = SimpleProperty<BinType>(BinType.Fixed)
    override val scale: MutableProperty<Transform> = SimpleProperty(NumericTransform.IDENTITY)
    val dataFrameListener: DataFrameListener<Row, C> = object : DataFrameListener<Row, C> {
        override fun dataFrameChanged(event: DataFrameEvent<Row, C>) {
            fireAxisChanged()
        }
    }

    protected open fun createBoundedInterval(): MutableBoundedInterval {
        val interval: MutableBoundedInterval = SimpleBoundedInterval(min, max - min, min, max)
        interval.minimumExtent = -(max - min)
        registerInterval(interval)
        return interval
    }

    protected open fun createScaledBoundedInterval(): MutableBoundedInterval {
        val interval: MutableBoundedInterval = TransformBoundedInterval(interval, scale)
        return interval
    }

    override val isNumerical: Boolean
        get() = true
    override val name: String
        get() = dataFrame.getColumnName(column)!!
    override val type: KClass<Any>
        get() = dataFrame.getColumnClass(column) as KClass<Any>

    override operator fun iterator(): Iterator<Row> {
        return dataFrame.rows().iterator()
    }

    override fun getValue(row: Row): Number? {
        return series[DATA]!!.get(row) as Number?
    }

    override fun getValue(layer: Int, row: Row): Number? {
        val s: Series<Row,out Any?>? = series[layer]
        return if (s != null) {
            s.get(row) as Number?
        } else {
            null
        }
    }

    override val formattedMinimum: String
        get() = getFormattedValue(minimum)
    override val formattedMaximum: String
        get() = getFormattedValue(maximum)

    override fun getFormattedValue(row: Row): String {
        return getFormattedValue(DATA, row)!!
    }

    override fun getFormattedValue(layer: Int, row: Row): String {
        val value: Any? = series[layer]!!.get(row)
        return if (value != null) {
            if (value is Number) {
                if (format != null) {
                    format!!.formatHtml(value, true)!!
                } else {
                    value.toString()
                }
            } else {
                value.toString()
            }
        } else {
            ""
        }
    }

    override fun getFormattedValue(value: Double): String {
        return if (format != null) {
            format!!.formatHtml(value, true)!!
        } else {
            value.toString()
        }
    }

    override fun getOriginalFloorValue(value: Double): Any? {
        return value
    }

    override fun getOriginalCeilValue(value: Double): Any? {
        return value
    }

    override var minimum: Double
        get() {
            scaledInterval
            return min
        }
        set(min) {
            if (this.min != min) {
                this.min = min
                interval.setMinMax(min, max)
                interval.minimumExtent = -(max - min)
                fireAxisChanged()
            }
        }
    override var maximum: Double
        get() {
            scaledInterval
            return max
        }
        set(max) {
            if (this.max != max) {
                this.max = max
                interval.setMinMax(min, max)
                interval.minimumExtent = -(max - min)
                fireAxisChanged()
            }
        }
    override val isDegenerate: Boolean
        get() {
            scaledInterval
            return max == min
        }

    override fun setMinMax(min: Double, max: Double) {
        if (this.max != max || this.min != min) {
            this.min = min
            this.max = max
            interval.setMinMax(min, max)
            interval.minimumExtent = if (max >= min) 0.0 else max - min
            interval.maximumExtent = if (max >= min) max - min else 0.0
            fireAxisChanged()
        }
    }

    override val columnStatistics: ColumnStatistics
        get() = DataFrameHelper.getColumnStatistics(dataFrame as DataFrame<*, C, Any?>, column)
    override val rowCount: Int
        get() = dataFrame.rowCount

    override fun getRow(row: Int): Row {
        return dataFrame.getRowKey(row)
    }

    override fun toString(): String {
        return name
    }

    init {
        this.dataFrame = dataFrame
        series = arrayOfNulls<Series<Row,out Any?>>(2)
        series[0] = dataFrame.getColumn(column)
        series[1] = if (annotationDataFrame != null) annotationDataFrame.getColumn(column) as Series<Row, out Any?> else null
        dataFrame.addWeakDataFrameListener(dataFrameListener)
        var min = Double.MAX_VALUE
        var max = -Double.MAX_VALUE
        var sum = 0.0
        var count = 0
        var uncorrectedSumOfSquares = 0.0
        for (r in dataFrame.rows()) {
            val value = getValue(r)
            if (value != null) {
                val v = value.toDouble()
                if (v < min) {
                    min = v
                }
                if (v > max) {
                    max = v
                }
                sum += v
                uncorrectedSumOfSquares += v * v
                count++
            }
        }
        this.min = min
        this.max = max
        val mean = sum / count
        this.mean = mean
        this.stdDev = sqrt(uncorrectedSumOfSquares / count - mean * mean)
        binCount.addPropertyListener(object : PropertyListener<Int> {
            override fun propertyChanged(event: PropertyEvent<Int>) {
                fireAxisChanged()
            }
        })
    }
}